// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { apiClient, API_ENDPOINTS } from '../config/api';

/** Format FastAPI / axios error detail for display */
function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (d == null) {
    return err?.message || 'Request failed';
  }
  if (typeof d === 'string') {
    return d;
  }
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'object' && x?.msg ? x.msg : String(x))).join('; ');
  }
  return String(d);
}

/**
 * Dialog for overriding matrix numeric scores or boolean Yes/No qualification.
 * RBAC: QA_SCORE_OVERRIDE (and team manager enforced server-side).
 */
const ScoreOverrideDialog = ({
  open,
  onClose,
  metric,
  taskId,
  onSuccess,
  variant = 'matrix',
}) => {
  const [overriddenScore, setOverriddenScore] = useState('');
  const [overriddenResult, setOverriddenResult] = useState('Yes');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isBoolean = variant === 'boolean';

  const getOriginalScore = () => {
    if (!metric) return 0;
    let raw;
    if (metric.is_overridden && metric.override_info) {
      raw = metric.override_info.original_score;
    } else if (typeof metric.score === 'string') {
      const scoreMap = {
        poor: 25,
        fair: 50,
        good: 75,
        excellent: 100,
      };
      raw = scoreMap[metric.score.toLowerCase()] ?? metric.score_value ?? metric.score ?? 0;
    } else {
      raw = metric.score_value ?? metric.score ?? 0;
    }
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  const originalScore = getOriginalScore();

  const getOriginalResult = () => {
    if (!metric) return 'N/A';
    if (metric.is_overridden && metric.override_info?.original_result != null) {
      return String(metric.override_info.original_result);
    }
    return metric.result != null ? String(metric.result) : 'N/A';
  };

  useEffect(() => {
    if (!open || !metric) {
      setOverriddenScore('');
      setOverriddenResult('Yes');
      setReason('');
      setError(null);
      return;
    }
    if (isBoolean) {
      if (metric.is_overridden && metric.override_info?.overridden_result) {
        setOverriddenResult(String(metric.override_info.overridden_result));
        setReason(metric.override_info.reason || '');
      } else {
        setOverriddenResult(metric.result === 'No' ? 'Yes' : 'No');
        setReason('');
      }
    } else if (metric.is_overridden && metric.override_info) {
      setOverriddenScore(String(metric.override_info.overridden_score));
      setReason(metric.override_info.reason || '');
    } else {
      setOverriddenScore('100');
      setReason('');
    }
    setError(null);
  }, [open, metric?.metric_key, metric?.is_overridden, isBoolean]);

  const resetFormAndClose = () => {
    setOverriddenScore('');
    setOverriddenResult('Yes');
    setReason('');
    setError(null);
    onClose();
  };

  const handleClose = () => {
    if (!loading) {
      resetFormAndClose();
    }
  };

  const handleSubmit = async () => {
    if (!metric || !metric.metric_key) {
      setError('Invalid metric. Please close and try again.');
      return;
    }
    if (!reason.trim()) {
      setError('Reason is required');
      return;
    }

    if (!taskId || String(taskId).trim() === '') {
      setError('Missing task ID. Close this dialog and reopen the transcription details.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let payload;
      if (isBoolean) {
        if (overriddenResult !== 'Yes' && overriddenResult !== 'No') {
          setError('Select Yes or No');
          setLoading(false);
          return;
        }
        payload = {
          metric_key: metric.metric_key,
          overridden_result: overriddenResult,
          reason: reason.trim(),
        };
      } else {
        const score = parseFloat(overriddenScore);
        if (Number.isNaN(score) || score < 0 || score > 100) {
          setError('Score must be between 0 and 100');
          setLoading(false);
          return;
        }
        payload = {
          metric_key: metric.metric_key,
          overridden_score: score,
          reason: reason.trim(),
        };
      }

      const response = await apiClient.patch(
        `${API_ENDPOINTS.VOICE_ANALYSIS_OVERRIDE}/${taskId}/analysis/override-score`,
        payload
      );

      const body = response.data;
      const nextAnalysis = body?.analysis;
      const ok =
        response.status === 200 &&
        nextAnalysis &&
        typeof nextAnalysis === 'object' &&
        (body.success === true || body.success === undefined);

      if (ok) {
        onSuccess(nextAnalysis);
        resetFormAndClose();
      } else {
        setError(body?.message || 'Failed to apply override');
      }
    } catch (err) {
      setError(formatApiError(err) || 'Failed to apply override. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isBoolean ? 'Override qualification: ' : 'Override score: '}
        {metric?.metric_name}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Metric
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {metric?.metric_name}
            </Typography>
            {!isBoolean && metric?.weight_percentage && (
              <Typography variant="caption" color="text.secondary">
                Weight: {metric.weight_percentage}%
              </Typography>
            )}
          </Box>

          {isBoolean ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Current result (AI)
              </Typography>
              <Typography variant="h6" color="text.primary">
                {getOriginalResult()}
              </Typography>
              <FormControl component="fieldset" sx={{ mt: 2 }} disabled={loading}>
                <FormLabel component="legend">Override to</FormLabel>
                <RadioGroup
                  row
                  value={overriddenResult}
                  onChange={(e) => setOverriddenResult(e.target.value)}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes (pass)" />
                  <FormControlLabel value="No" control={<Radio />} label="No (fail)" />
                </RadioGroup>
              </FormControl>
            </Box>
          ) : (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Original score
                </Typography>
                <Typography variant="h6" color="text.primary">
                  {originalScore.toFixed(1)} / 100
                </Typography>
              </Box>
              <TextField
                label="New score"
                type="number"
                value={overriddenScore}
                onChange={(e) => setOverriddenScore(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 0.1 }}
                fullWidth
                required
                helperText="Enter a score between 0 and 100"
                disabled={loading}
              />
            </>
          )}

          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={4}
            fullWidth
            required
            helperText="Required for audit trail"
            disabled={loading}
            error={!reason.trim() && error === 'Reason is required'}
          />

          {error && <Alert severity="error">{error}</Alert>}

          <Alert severity="info">
            This override will be logged with your user ID and timestamp for audit purposes.
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !reason.trim()}
        >
          {loading ? 'Saving...' : 'Save override'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScoreOverrideDialog;
